import {
  Box,
  createStyles,
  Grid,
  Hidden,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import { addDays, parse } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { convertValidationRules } from '../../../hooks/useFormState';
import { AddressAndDate } from '../../../hooks/useSSMStartService';
import { Meter } from '../../../components/ssm/ssm.types';
import { useTranslation } from '../../../hooks/useTranslation';
import {
  DEFAULT_MAP_CENTER,
  isLocationWithinPGEServiceBoundary,
} from '../../../util/map-utils';
import {
  Maybe,
  PremiseSpDetails,
  SearchServiceAddress,
  ServiceAddress,
} from '../../../__generated__/pge-types';
import DatePicker from '../../date-picker';
import ServiceAddressLookup from '../../service-address-lookup/ServiceAddressLookup';
import { WizardFormProps } from '../../utility/wizard/wizard.types';
import {
  validateStartDate,
  validateAddress,
} from '../common/SSMStartService.rules';
import { AddressSuggestion } from './AddressSuggestion/AddressSuggestion';
import { MultipleMeters } from './MultipleMeters/MultipleMeters';
import { usePremiseSearchQuery } from '../../../hooks/usePremiseSearchQuery';
import Backdrop from '../../backdrop';
import useUtil from '../../need-more-time-to-pay/useUtil';
import colors from '../../../themes/main-colors';
import TextLink from '../../text-link';
import { SelectedMetersList } from './SelectedMetersList/SelectedMetersList';
import useAuth from '../../../hooks/useAuth';
import useAccountCustomer from '../../../hooks/useAccountCustomer';
import useGetAccountEligibiltyQuery from '../../../hooks/useGetAccountEligibiltyQuery';
import { navigate } from 'gatsby';
import ROUTES from '../../../routes';
import { ExpandMore, ExpandLess } from '@material-ui/icons';
import StaticMap from '../../static-map';

const DATE_FORMAT = 'MM/dd/yyyy';
export const ALLOWED_RES_METER_TYPES = ['E-M-RES', 'E-UB-LT'];

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    rootContainer: () => ({
      '& .mapContainerRoot': {
        minHeight: '300px',
      },
    }),
    header: {
      fontWeight: 'bold',
      fontSize: '20px',
    },
    subHeader: {
      display: 'block',
      fontWeight: 'bold',
      fontSize: '16px',
      marginTop: '10px',
      marginBottom: '-10px',
      width: '100%',
    },
    selectedMetersListAddress: {
      display: 'block',
      fontWeight: 'bold',
      fontSize: '16px',
      marginTop: '10px',
      marginBottom: '10px',
      width: '100%',
    },
    marginBottom: {
      marginBottom: '-15px',
    },
    selectedMetersList: {
      display: 'flex',
      backgroundColor: colors.modalBackgroundColor,
      marginTop: '20px',
      padding: '6px 0 6px 15px',
      borderRadius: '6px',
      boxShadow: `0px 3px 6px ${colors.boxShadowColor}`,
    },
    editMeterSelections: {
      width: '100%',
    },
    divider: {
      width: '97%',
      color: colors.lightGray5,
    },
    sameAddressError: {
      color: colors.roseRed,
      marginLeft: '14px',
      marginRight: '14px',
      fontSize: '0.75rem',
    },
  }),
);

export const SSMAddressAndDateForm = ({
  formState,
  componentProps,
}: WizardFormProps<AddressAndDate, ServiceAddress & { date?: Date }>) => {
  const { t, richT } = useTranslation();
  const classes = useStyles();
  const { onChange } = formState.props('date');

  const {
    isAddressVerified,
    setIsAddressVerified,
    isMeterSelectionVerified,
    setIsMeterSelectionVerified,
    isAddressSuggestionModalDisplayed,
    setIsAddressSuggestionModalDisplayed,
    autoCompleteAddress,
    setAutoCompleteAddress,
    isMultipleMetersModalDisplayed,
    setIsMultipleMetersModalDisplayed,
    metersInfo,
    setMetersInfo,
    currentPosition,
    setCurrentPosition,
    setPersonName,
    handleMoveToAddress,
    isSSMMoveFlow,
    setMultiplePremisesIneligible,
    showSameAddressError,
    setShowSameAddressError,
    fromAddr,
  } = componentProps;

  const { setErrorNotification } = useUtil();

  const {
    setPremiseIds,
    loading: premiseSearchLoading,
    error,
    data,
  } = usePremiseSearchQuery();

  const [isLoaded, setLoaded] = useState(false);
  const [isAddressHelpDisplayed, setIsAddressHelpDisplayed] = useState<boolean>(
    false,
  );

  const [addressSuggestions, setAddressSuggestions] = useState<
    Maybe<SearchServiceAddress>[]
  >([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [serviceAddressQueryLoader, setServiceAddressQueryLoader] = useState<
    boolean
  >(false);
  const [selectedMeters, setSelectedMeters] = useState<string[]>([]);
  const [validateAccountLoading, setValidateAccountLoading] = useState<boolean>(
    false,
  );
  const { isAuthenticated } = useAuth();
  const {
    customer: customerInfoData,
    loading: customerInfoLoading,
    error: customerInfoError,
  } = useAccountCustomer();
  const { refetch: validateAccount } = useGetAccountEligibiltyQuery();

  const handleAddressSelected = (address: SearchServiceAddress) => {
    if (isSSMMoveFlow) {
      setShowSameAddressError(false);
    }
    let completeAddress: string = '';
    if (address.addressLine1 && address.city && address.postal) {
      completeAddress = `${address.addressLine1.trim()}, ${address.city.trim()}, ${address.postal.trim()}`;
    }
    setAutoCompleteAddress(completeAddress);
    void formState.setValue('addressLine1', completeAddress);
    setAddressSuggestions([]);
    setIsAddressVerified(true);
    setIsAddressSuggestionModalDisplayed(false);
    if (address && Number(address.premiseIds?.length) === 1) {
      setMultiplePremisesIneligible(false);
      if (!isSSMMoveFlow) {
        setPremiseIds(address?.premiseIds!);
      }
      if (isSSMMoveFlow) {
        handleMoveToAddress(address);
        if (
          fromAddr.addressLine1.trim().toLowerCase() ===
            address.addressLine1.trim().toLowerCase() &&
          fromAddr.city.trim().toLowerCase() ===
            address.city.trim().toLowerCase() &&
          fromAddr.postal.trim().toLowerCase() ===
            address.postal.trim().toLowerCase()
        ) {
          setShowSameAddressError(true);
        } else {
          setShowSameAddressError(false);
          setPremiseIds(address?.premiseIds!);
        }
      }
    } else if (address && Number(address.premiseIds?.length) > 1) {
      setMultiplePremisesIneligible(true);
      setCurrentPosition(undefined);
    }
    setIsMeterSelectionVerified(false);
  };

  const handleMetersSelected = (_selectedMeters: string[]) => {
    if (_selectedMeters.length) {
      const _metersInfo: Meter[] = [...metersInfo];
      _metersInfo.forEach((meter: Meter) => {
        if (_selectedMeters.includes(meter.spId)) {
          meter.checked = !meter.disabled;
        } else {
          meter.checked = false;
        }
      });
      setMetersInfo(_metersInfo);
      setIsMeterSelectionVerified(true);
      setIsMultipleMetersModalDisplayed(false);
    }
  };

  const handleEditSelections = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const _selectedMeters = metersInfo
      .filter((meter: Meter) => meter.checked)
      .map((meter: Meter) => meter.spId);
    setSelectedMeters(_selectedMeters);
    setIsMultipleMetersModalDisplayed(true);
  };

  useEffect(() => {
    if (error || customerInfoError) {
      setErrorNotification(true);
    } else {
      setErrorNotification(false);
    }
  }, [error, customerInfoError]);

  useEffect(() => {
    const meters: Meter[] = [];

    data?.premiseSearch?.forEach(premise => {
      premise?.spList?.forEach((sp: Maybe<PremiseSpDetails>) => {
        const meter = {
          premiseId: premise?.premId || '',
          spId: sp?.spId || '',
          spDesc: sp?.spDesc || '',
          spType: sp?.spType || '',
          locationDesc: sp?.locationCode || '',
          geolocation: sp?.geolocation || '',
          meterNumber: sp?.meterNumber || '',
          disabled:
            !ALLOWED_RES_METER_TYPES.includes(String(sp?.spType)) ||
            sp?.spStatus !== 'ACTIVE' ||
            (sp?.spType === 'E-UB-LT' && sp?.isResAreaLight === false),
          checked: false,
          isAreaLight: sp?.isResAreaLight || false,
        };
        meters.push(meter);
      });
    });
    if (meters.length > 0) {
      setMetersInfo(meters);
      const [latitude, longitude] = meters[0].geolocation.split(',');
      setCurrentPosition({
        lat: Number(latitude),
        lng: Number(longitude),
      });
    }

    if (meters.length === 1 && !meters[0].disabled) {
      const singleMeter: Meter = meters[0];
      singleMeter.checked = true;
      setMetersInfo([singleMeter]);
      setIsMeterSelectionVerified(true);
    }
    if (meters.length > 0) {
      const isEveryMeterDisabled = meters.every(
        meter => meter.disabled === true,
      );
      if (isEveryMeterDisabled) {
        setIsMeterSelectionVerified(false);
      }
      if (isEveryMeterDisabled && meters.length < 10) {
        setIsMultipleMetersModalDisplayed(false);
      } else if (meters.length > 1) {
        setIsMultipleMetersModalDisplayed(true);
      }
    }
  }, [data]);

  useEffect(() => {
    const {
      values: { addressLine1 },
    } = formState;
    if (addressLine1) {
      setInputValue(addressLine1);
    } else {
      setInputValue('');
    }
    if (isAddressVerified && addressLine1 !== autoCompleteAddress) {
      setIsAddressVerified(false);
    }
  }, [formState.values, formState.errors]);

  useEffect(() => {
    if (isLoaded) {
      navigator.geolocation.getCurrentPosition(position => {
        const { latitude, longitude } = position.coords;
        if (
          isLocationWithinPGEServiceBoundary({ lat: latitude, lng: longitude })
        ) {
          setCurrentPosition({
            lat: latitude,
            lng: longitude,
          });
        }
      });
    }
  }, [isLoaded]);

  return customerInfoLoading || validateAccountLoading ? (
    <Backdrop forceOpen message={t('LOADING')} />
  ) : (
    <>
      {premiseSearchLoading ? (
        <Backdrop forceOpen message={t('LOADING')} />
      ) : null}
      <Grid container direction="column" spacing={4}>
        <Grid item container spacing={2}>
          <Grid container item>
            <Hidden smDown>
              <Typography className={classes.header}>
                {t('WHAT_ADDRESS_YOU_WANT_TO_START_SERVICE')}
              </Typography>
            </Hidden>
            <Typography>{t('FOR_BEST_RESULTS_SELECT_AN_OPTION')}</Typography>
            <Box>
              <a
                href="#"
                onClick={(e: any) => {
                  e.preventDefault();
                  setIsAddressHelpDisplayed(!isAddressHelpDisplayed);
                }}
                style={{
                  cursor: 'pointer',
                  textDecoration: 'none',
                  display: 'inline',
                }}
              >
                <Box display="flex">
                  <span
                    style={{
                      color: colors.sparkBlue,
                      fontSize: '1rem',
                    }}
                  >
                    {t('MORE_ADDRESS_HELP')}
                  </span>
                  {isAddressHelpDisplayed ? <ExpandLess /> : <ExpandMore />}
                </Box>
              </a>
              {isAddressHelpDisplayed && (
                <ul style={{ margin: '0px' }}>
                  <li>{richT('CHECK_TO_SEE_IF_THE_ADDRESS')}</li>
                  <li>
                    {richT('CALL_US_AT', {
                      CUSTOMER_SERVICE_NUMBER: t('CALL_FOR_ASSISTANCE_NUMBER'),
                    })}
                  </li>
                </ul>
              )}
            </Box>
          </Grid>
          <Grid container item spacing={0} direction={'column'}>
            <Grid item>
              <ServiceAddressLookup
                onAddressChange={handleAddressSelected}
                inputFieldProps={{
                  variant: 'outlined',
                  label: t('STREET_ADDRESS'),
                  ...formState.props('addressLine1'),
                }}
                autocompleteProps={{
                  freeSolo: false,
                  inputValue,
                }}
                setAddressSuggestions={setAddressSuggestions}
                setServiceAddressQueryLoader={setServiceAddressQueryLoader}
              />
              {showSameAddressError && (
                <Typography className={classes.sameAddressError}>
                  {t('YOU_ALREADY_HAVE_ACTIVE_SERVICE')}
                </Typography>
              )}
            </Grid>
            <Grid item className={classes.rootContainer}>
              <StaticMap
                center={currentPosition ?? DEFAULT_MAP_CENTER}
                marker={currentPosition}
                onLoad={() => setLoaded(true)}
              />
            </Grid>
            {isMeterSelectionVerified && Number(metersInfo?.length) > 1 ? (
              <Grid item>
                <Typography className={classes.subHeader}>
                  {t('START_SERVICE_FOR')}
                </Typography>
                <Grid container className={classes.selectedMetersList}>
                  <Typography className={classes.selectedMetersListAddress}>
                    {`${autoCompleteAddress}`}
                  </Typography>
                  <Box className={classes.editMeterSelections}>
                    <TextLink onClick={handleEditSelections}>
                      {t('EDIT_SELECTIONS')}
                    </TextLink>
                  </Box>
                  <Grid item className={classes.divider}>
                    <hr />
                  </Grid>
                  {metersInfo
                    .filter((meter: Meter) => meter.checked)
                    .map((meter: Meter, index: number) => (
                      <SelectedMetersList
                        meter={meter}
                        index={index}
                        key={meter.spId}
                      />
                    ))}
                </Grid>
              </Grid>
            ) : null}
          </Grid>
        </Grid>
        <Grid item container spacing={2}>
          <Grid item container>
            <Typography className={`${classes.header} ${classes.marginBottom}`}>
              {t('WHEN_DO_YOU_NEED_SERVICE')}
            </Typography>
          </Grid>
          <Grid item container>
            <DatePicker
              style={{ width: '100%' }}
              label={t('START_SERVICE_DATE')}
              name="date"
              minDate={new Date()}
              format={DATE_FORMAT}
              maxDate={addDays(new Date(), 60)}
              {...formState.props('date')}
              onChange={async ({
                target: { value },
              }: {
                target: { value: string };
              }) => {
                // TODO - Need to parse the value because
                // DatePicker onChange is called with string
                // It is not clear yet what the new backend start service
                // submit will accept but current placeholder is of type Date
                // if backend expects MM/DD/YYYY string type, then we can store
                // the type as string in state and get rid of this conversion
                await onChange(parse(value, DATE_FORMAT, new Date()));
              }}
              data-cy="start-field"
              mask={'__/__/____'}
              labelShrink={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Grid>
      <AddressSuggestion
        isOpen={isAddressSuggestionModalDisplayed}
        handleOnModalCancelled={() =>
          setIsAddressSuggestionModalDisplayed(false)
        }
        userAddress={formState.values.addressLine1 || ''}
        loading={serviceAddressQueryLoader}
        addressSuggestions={addressSuggestions}
        handleSuggestedAddressSubmit={handleAddressSelected}
      />
      <MultipleMeters
        isOpen={isMultipleMetersModalDisplayed}
        handleClose={() => setIsMultipleMetersModalDisplayed(false)}
        handleSubmit={handleMetersSelected}
        userAddress={formState.values.addressLine1 || ''}
        meters={metersInfo}
        selectedMeters={selectedMeters}
        setSelectedMeters={setSelectedMeters}
      />
    </>
  );
};

export const createAddressAndDateFormAdapter = () => {
  return {
    toFormState: (
      initial: AddressAndDate,
    ): ServiceAddress & { date?: Date } => ({
      date: initial.date,
      addressLine1: initial.address?.addressLine1,
    }),
    fromFormState: (
      state: ServiceAddress & { date?: Date },
    ): AddressAndDate => {
      return {
        address: {
          addressLine1: state.addressLine1,
        },
        date: state.date,
      };
    },
  };
};

export const createAddressAndDateValidateFunction = () =>
  convertValidationRules<{
    addressLine1: string;
    date: Date;
  }>(() => {
    return {
      addressLine1: validateAddress,
      date: validateStartDate,
    };
  });
